import * as React from "react"
import {clsx} from "clsx"
import styles from "./PasswordStrengthMeter.module.css"

/** Named strength levels the meter can render, weakest to strongest. */
export type StrengthLevel = "very-weak" | "weak" | "medium" | "strong" | "very-strong"

interface PasswordStrengthMeterProps extends React.ComponentPropsWithoutRef<"div"> {
	/** Reached strength level, `0` for none. Fills that many segments. */
	value: number
	/** Number of segments / the highest reachable level. */
	max?: number
	/**
	 * Named level (e.g. `"weak"`, `"very-strong"`), surfaced as `data-level`. Drives
	 * the per-level fill colour (red → amber → green) and doubles as a styling/testing
	 * hook; the segment count from `value` conveys strength as well.
	 */
	level?: StrengthLevel | null
	/** Text shown beneath the bar and announced to assistive tech via `aria-valuetext`. */
	label?: React.ReactNode
}

/**
 * A segmented password-strength bar, authored in the Base UI style — Base UI ships
 * no strength meter. Presentation only: hand it a computed `value`/`max`/`level`
 * (see `usePasswordStrength`) and it renders the accessible progress bar; it does
 * not itself score anything. Sits as a sibling to the password control.
 */
const PasswordStrengthMeter = React.forwardRef<HTMLDivElement, PasswordStrengthMeterProps>(
	function PasswordStrengthMeter(
		{value, max = 5, level = null, label, className, "aria-label": ariaLabel = "Password strength", ...props},
		ref,
	) {
		const filled = Math.max(0, Math.min(value, max))
		return (
			<div
				ref={ref}
				data-slot="password-strength-meter"
				data-level={level ?? undefined}
				className={clsx(styles.root, className)}
				{...props}
			>
				<div
					className={styles.track}
					role="progressbar"
					aria-label={ariaLabel}
					aria-valuemin={0}
					aria-valuemax={max}
					aria-valuenow={filled}
					aria-valuetext={typeof label === "string" ? label : undefined}
				>
					{Array.from({length: max}, (_, index) => (
						<span
							key={index}
							className={styles.segment}
							data-active={index < filled ? "" : undefined}
						/>
					))}
				</div>
				{label != null && (
					<div data-slot="password-strength-label" className={styles.label}>
						{label}
					</div>
				)}
			</div>
		)
	},
)

export {PasswordStrengthMeter}
export type {PasswordStrengthMeterProps}
